import type { Variant } from 'types';
import { useState } from 'react';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Copy, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from 'helpers/cn';
import { toast } from 'sonner';
import { FOCUS_RING } from '../../ConsolePanel';

type CodeTab = 'js' | 'css' | 'html';

// border-input, not border-line: these carry no fill, so the outline is the only thing
// drawing them. h-9 below `md` clears the 36px touch floor; the desktop height is as was.
const CHROME_BUTTON = cn(
  'border-input text-dim hover:text-foreground hover:bg-hover flex h-9 shrink-0 items-center gap-1.5 rounded-lg',
  'border bg-transparent px-2.5 text-meta transition-colors duration-150 md:h-7',
  FOCUS_RING,
);

type CodeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: Variant;
};

type CodePanel = { value: CodeTab; label: string; code: string };

/** Only the panels this variant actually has code for, so no tab opens empty. */
function buildCodePanels(variant: Variant): CodePanel[] {
  const panels: CodePanel[] = [];

  if (variant.globalJavascript) panels.push({ value: 'js', label: 'JavaScript', code: variant.globalJavascript });
  if (variant.globalCSS) panels.push({ value: 'css', label: 'CSS', code: variant.globalCSS });

  if (variant.changes?.length) {
    const html = variant.changes
      .map((c) => `<!-- ${c.action} ${c.friendlySelector || c.selector || ''} -->\n${c.value || ''}`)
      .join('\n\n');
    panels.push({ value: 'html', label: 'HTML Changes', code: html });
  }

  return panels;
}

export const CodeModal = ({ open, onOpenChange, variant }: CodeModalProps) => {
  const [tab, setTab] = useState<CodeTab>('js');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const tabs = buildCodePanels(variant);
  const currentCode = tabs.find((t) => t.value === tab)?.code || tabs[0]?.code || '';
  const activeTab = tabs.find((t) => t.value === tab) ? tab : tabs[0]?.value || 'js';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentCode);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (tabs.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          '[&_[aria-label=Close]]:hidden',
          isFullscreen ? 'h-[90vh] w-[95vw] max-w-[95vw]' : 'sm:max-w-4xl',
          'bg-panel border-line rounded-panel overflow-hidden border p-0',
        )}
      >
        <div className="flex h-full flex-col">
          {/* min-w-0 on the label so `truncate` has something to shrink against, and the
              button group is shrink-0 so it never gets squeezed off the right edge. */}
          <div className="border-line bg-panel2 flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5">
            <span className="micro-label min-w-0 truncate" title={`${variant.name} · Code`}>
              {variant.name} · Code
            </span>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className={CHROME_BUTTON}
                aria-label={isFullscreen ? 'Leave fullscreen' : 'Go fullscreen'}
                onClick={() => setIsFullscreen(!isFullscreen)}
              >
                {isFullscreen ? (
                  <Minimize2 className="size-3.5" strokeWidth={1.5} />
                ) : (
                  <Maximize2 className="size-3.5" strokeWidth={1.5} />
                )}
              </button>
              <button type="button" className={CHROME_BUTTON} onClick={copyToClipboard}>
                <Copy className="size-3.5" strokeWidth={1.5} />
                Copy
              </button>
              <DialogClose asChild>
                <button type="button" className={CHROME_BUTTON}>
                  Close
                </button>
              </DialogClose>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => setTab(v as CodeTab)} className="w-full">
            <div className="px-3 pt-3">
              <TabsList className="h-auto gap-1 bg-transparent p-0">
                {tabs.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className="micro-label border-line rounded-pill data-[state=active]:bg-sel-bg data-[state=active]:border-sel-line data-[state=active]:text-foreground border px-[11px] py-[6px] data-[state=active]:shadow-none"
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {tabs.map((t) => (
              <TabsContent key={t.value} value={t.value} className="m-0 p-3">
                <pre
                  className={cn(
                    'log-line bg-field border-line rounded-card overflow-auto border px-3 py-2.5',
                    isFullscreen ? 'h-[calc(90vh-148px)]' : 'max-h-[60vh]',
                  )}
                >
                  <code>{t.code}</code>
                </pre>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
